import { BULK_READY_FOR_PROMOTION_QUEUE_TABLE_QUERY } from '@/components/bulkQueue/tables/readyForPromotion/BulkReadyForPromotionQueueTable';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useState } from 'react';

import { errorToast, successToast, warningToast } from '../../../toast';

// Individual Visit Request Promotion Mutation
export const usePromoteVisitRequest = ({ visitRequestId }: { visitRequestId: string }) => {
    const [promoteVisitRequest, results] = useMutation(PROMOTE_VISIT_REQUEST_MUTATION, {
        variables: {
            visitRequestId,
        },
        onError: (error) => {
            if (error instanceof Error) {
                errorToast(error.message);
            }
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_READY_FOR_PROMOTION_QUEUE_TABLE_QUERY],
    });

    return { promoteVisitRequest, ...results };
};

// Bulk Visit Request Promotion Mutation
export const useBulkPromoteVisitRequestsMutation = () => {
    const [mutate, results] = useMutation(BULK_PROMOTE_VISIT_REQUESTS_MUTATION, {
        onError: (error) => {
            if (error instanceof Error) {
                errorToast(error.message);
            }
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_READY_FOR_PROMOTION_QUEUE_TABLE_QUERY],
    });

    const promoteVisitRequests = async ({ visitRequestIds }: { visitRequestIds: string[] }) => {
        return mutate({
            variables: {
                visitRequests: visitRequestIds.map((id) => ({ visitRequestId: id })),
            },
        });
    };

    return { promoteVisitRequests, ...results };
};

// Multiple Visit Request Promotion using Individual Mutations
export const usePromoteMultipleVisitRequests = () => {
    const client = useApolloClient();
    const [loading, setLoading] = useState(false);

    const promoteMultipleVisitRequests = async ({
        visitRequestIds,
    }: {
        visitRequestIds: string[];
    }) => {
        setLoading(true);

        try {
            const results = await Promise.allSettled(
                visitRequestIds.map((visitRequestId) =>
                    client.mutate({
                        mutation: PROMOTE_VISIT_REQUEST_MUTATION,
                        variables: { visitRequestId },
                    }),
                ),
            );

            const successCount = results.filter(
                (result) => result.status === 'fulfilled' && !result.value.errors,
            ).length;
            const errorCount = results.length - successCount;

            if (successCount === results.length) {
                successToast(
                    `Successfully promoted ${successCount} visit request${
                        successCount === 1 ? '' : 's'
                    }`,
                );
            } else if (errorCount === results.length) {
                errorToast(
                    `Failed to promote all ${errorCount} visit request${
                        errorCount === 1 ? '' : 's'
                    }`,
                );
            } else {
                warningToast(
                    `Promoted ${successCount} visit request${
                        successCount === 1 ? '' : 's'
                    }, ${errorCount} failed`,
                );
            }

            // Wait for the table + counts to refresh before resolving so callers
            // that clear selection on completion don't flash stale state.
            await client
                .refetchQueries({
                    include: [ALL_VISIT_COUNTS, BULK_READY_FOR_PROMOTION_QUEUE_TABLE_QUERY],
                })
                .catch(() => undefined);

            return { successCount, errorCount };
        } finally {
            setLoading(false);
        }
    };

    return { promoteMultipleVisitRequests, loading };
};

const VisitRequestPromotionFragment = gql`
    fragment VisitRequestPromotionFragment on VisitRequest {
        id
        state
        workflowStep
        visit {
            id
        }
    }
`;

const PROMOTE_VISIT_REQUEST_MUTATION = gql`
    mutation PromoteVisitRequest($visitRequestId: UUID!) {
        promoteVisitRequest(input: { visitRequestId: $visitRequestId }) {
            ...VisitRequestPromotionFragment
        }
    }
    ${VisitRequestPromotionFragment}
`;

const BULK_PROMOTE_VISIT_REQUESTS_MUTATION = gql`
    mutation BulkPromoteVisitRequests($visitRequests: [PromoteVisitRequestInput!]!) {
        bulkPromoteVisitRequests(input: { visitRequests: $visitRequests }) {
            results {
                resource {
                    ...VisitRequestPromotionFragment
                }
                errors {
                    message
                }
                success
            }
        }
    }
    ${VisitRequestPromotionFragment}
`;
